/**
 * Google Consent Mode v2 helpers.
 * Docs: https://developers.google.com/tag-platform/security/concepts/consent-mode
 *
 * Signal-to-banner-level mapping:
 *   security_storage         — always granted (strictly necessary; level 1 Nødvendige).
 *                              Set at default time, never updated.
 *   analytics_storage        — granted iff analyticsAccepted (level 3 Statistikk).
 *   functionality_storage    — granted iff analyticsAccepted OR marketingAccepted.
 *                              Used by both analytics (language/region prefs) and
 *                              marketing (ad localization) tags — soft supporting signal.
 *   personalization_storage  — granted iff analyticsAccepted OR marketingAccepted.
 *                              Used by YouTube embeds, dynamic remarketing, and GA's
 *                              personalized-advertising features — soft supporting signal.
 *   ad_storage               — granted iff marketingAccepted (level 4 Markedsføring).
 *   ad_user_data             — granted iff marketingAccepted.
 *   ad_personalization       — granted iff marketingAccepted.
 *
 * Level 2 Funksjonelle does not gate any Google signal today — it feeds HubSpot only.
 */
import { shouldHonorDoNotTrack } from "#lib/hubspotConsent.js";

// Re-exported so the GTM component has a single import surface.
export { shouldHonorDoNotTrack };

export interface GoogleConsentState {
	analyticsAccepted: boolean;
	marketingAccepted: boolean;
}

export type ConsentValue = "granted" | "denied";

export interface ConsentUpdate {
	analytics_storage: ConsentValue;
	ad_storage: ConsentValue;
	ad_user_data: ConsentValue;
	ad_personalization: ConsentValue;
	functionality_storage: ConsentValue;
	personalization_storage: ConsentValue;
}

const v = (granted: boolean): ConsentValue => (granted ? "granted" : "denied");

export const buildConsentUpdate = (state: GoogleConsentState): ConsentUpdate => {
	const softSignal = v(state.analyticsAccepted || state.marketingAccepted);
	return {
		analytics_storage: v(state.analyticsAccepted),
		ad_storage: v(state.marketingAccepted),
		ad_user_data: v(state.marketingAccepted),
		ad_personalization: v(state.marketingAccepted),
		functionality_storage: softSignal,
		personalization_storage: softSignal,
	};
};

export interface DefaultConsent extends ConsentUpdate {
	security_storage: ConsentValue;
	wait_for_update: number;
}

export const defaultConsent: DefaultConsent = {
	analytics_storage: "denied",
	ad_storage: "denied",
	ad_user_data: "denied",
	ad_personalization: "denied",
	functionality_storage: "denied",
	personalization_storage: "denied",
	security_storage: "granted",
	wait_for_update: 500,
};

// Mount the GTM <script src> when EITHER:
//   - the visitor explicitly accepted marketing (consent overrides DNT), OR
//   - the browser is not signalling DNT/GPC (load GTM with denied defaults so
//     Consent Mode v2 cookieless pings can flow even before the banner is answered).
// Strict-privacy path: visitors with DNT/GPC who haven't accepted marketing never
// touch googletagmanager.com.
export const shouldLoadGtmScript = (marketingAccepted: boolean, dntRespected: boolean): boolean =>
	marketingAccepted || !dntRespected;
